// libs
import React, { useCallback } from 'react'

// constants
import { CLASS_PREFIX } from 'src/constants/'

// components

// helpers
import { NotificationsQuery } from '@lopo-tech/lopo-api'
import { useScrollListener } from 'src/hooks_shared/use_event_listener'
import { textResources } from 'src/lang/de'
import BookmarkNotification from './bookmark/bookmark_notification'
import CommentNotification from './comment/comment_notification'
import RecommentNotification from './comment/recomment_notification'
import FollowNotification from './follow/follow_notification'
import FolloweePostNotification from './follow/followee_post_notification'
import GroupNotification from './group/group_notification'
import Notification from './notification'
import './notifications.scss'
import ProjectContentNotification from './project/project_content_notification'
import ProjectMeetingNotification from './project/project_meeting_notification'
import ProjectMilestoneNotification from './project/project_milestone_notification'
import UnknownNotification from './unknown/unknown_notification'

type Notification = NotificationsQuery['notifications']['items'][number];

const cls = `${CLASS_PREFIX}notifications`
const clsEmptyNotifications = `${cls}__empty`
const clsList = `${cls}__list`

interface IProps {
  // array of notifications we want to render
  notifications: Notification[];
  // callback when we click on a Notification
  onNotificationClick: (notification: Notification) => void;
  // callback when we click on a NotificationButton
  fetchMoreNotifications: () => void;
}

const Notifications: React.FC<IProps> = ({
  fetchMoreNotifications,
  notifications,
  onNotificationClick
}) => {
  const listener = () => {
    fetchMoreNotifications()
  }

  useScrollListener(listener, { passive: true })

  const renderNotification = useCallback((notification: Notification) => {
    const notificationType = notification.__typename

    const handleNotificationClick = () => {
      onNotificationClick(notification)
    }

    switch (notificationType) {
      case 'NewReactionNotification': {
        const { createdAt, id, reactionName, reactable, reactor, readAt } =
          notification

        return (
          <BookmarkNotification
            avatarImage={reactor.avatar.previewUrl}
            reactable={reactable}
            createdAt={createdAt}
            initiatorName={reactor.name}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
            type={reactionName}
          />
        )
      }
      case 'ReplyToCommentNotification': {
        const { commenter, createdAt, id, message, readAt } = notification

        return (
          <RecommentNotification
            avatarImage={commenter.avatar.previewUrl}
            createdAt={createdAt}
            isRead={!!readAt}
            key={id}
            message={message}
            onClick={handleNotificationClick}
          />
        )
      }
      case 'NewCommentNotification': {
        const { commenter, createdAt, id, readAt, resourceLink } = notification
        const { contentItem } = resourceLink

        return (
          <CommentNotification
            avatarImage={commenter.avatar.previewUrl}
            contentItemTitle={contentItem.title}
            contentItemType={contentItem.__typename}
            createdAt={createdAt}
            initiatorName={commenter.name}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
          />
        )
      }

      case 'NewGroupContentNotification': {
        const { createdAt, group, id, readAt, resourceLink } = notification
        const { contentItem } = resourceLink

        return (
          <GroupNotification
            avatarImage={contentItem.author.avatar.previewUrl}
            contentItemTitle={contentItem.title}
            createdAt={createdAt}
            groupName={group.name}
            initiatorName={contentItem.author.name}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
          />
        )
      }
      case 'NewFollowerNotification': {
        const { createdAt, id, readAt, resourceLink } = notification
        const { actorProfile } = resourceLink

        return (
          <FollowNotification
            avatarImage={actorProfile.avatar.previewUrl}
            createdAt={createdAt}
            initiatorName={actorProfile.name}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
          />
        )
      }
      case 'NewFolloweePostNotification': {
        const { createdAt, id, readAt, resourceLink } = notification
        const { contentItem } = resourceLink

        return (
          <FolloweePostNotification
            avatarImage={contentItem.author.avatar.previewUrl}
            contentItemTitle={contentItem.title}
            contentItemType={contentItem.__typename}
            createdAt={createdAt}
            initiatorName={contentItem.author.name}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
          />
        )
      }
      case 'NewProjectContentNotification': {
        console.log('NewProjectContentNotification: ', notification)
        const { createdAt, id, readAt, project, resourceLink } = notification
        const { contentItem } = resourceLink

        return (
          <ProjectContentNotification
            createdAt={createdAt}
            isRead={!!readAt}
            key={id}
            onClick={handleNotificationClick}
            projectName={project.name}
            avatarImage={project.images[0]?.file.previewUrl}
            initiatorName={contentItem.author.name}
            contentItemTitle={contentItem.title}
          />
        )
      }
      case 'NewProjectMilestoneNotification': {
        console.log('NewProjectMilestoneNotification: ', notification)
        const { createdAt, id, readAt, milestone, project } = notification

        return (
          <ProjectMilestoneNotification
            createdAt={createdAt}
            isRead={!!readAt}
            key={id}
            milestoneName={milestone.title}
            onClick={handleNotificationClick}
            projectName={project.name}
            avatarImage={project.images[0]?.file.previewUrl}
          />
        )
      }
      case 'NewProjectMeetingNotification': {
        console.log('NewProjectMeetingNotification: ', notification)
        const { createdAt, id, readAt, project, meeting } = notification

        return (
          <ProjectMeetingNotification
            createdAt={createdAt}
            isRead={!!readAt}
            key={id}
            meetingTitle={meeting.title}
            onClick={handleNotificationClick}
            projectName={project.name}
            avatarImage={project.images[0]?.file.previewUrl}
          />
        )
      }
      // TODO: Following types are rendered as unknown notification.
      // Add support once support becomes available:
      // case MESSAGE:
      //   return <MessageNotification {...notification} key={index} onClick={onClick} />
      // case EVENT_PARTICIPATION:
      //   return (<EventParticipationNotification {...notification} key={index} onClick={onClick} />)
      default: {
        const { createdAt, id, readAt, message } = notification
        return (
          <UnknownNotification
            createdAt={createdAt}
            isRead={!!readAt}
            key={id}
            message={message}
            onClick={handleNotificationClick}
          />
        )
      }
    }
  }, [])

  return (
    <div className={cls}>
      {notifications.length
        ? (
          <ul className={clsList}>{notifications.map(renderNotification)}</ul>
        )
        : (
          <p className={clsEmptyNotifications}>
            {textResources.notification.noneYet}
          </p>
        )}
    </div>
  )
}

export default Notifications
